import { ref } from "vue";
import { useAuthenticatedFetch } from "../auth/useAuthenticatedFetch";
import type {
  SessionBooking,
  SearchBookingParams,
  StoreBookingPayload,
  MarkAttendancePayload,
} from "@/interfaces/sessionBooking";

import { sessionBookings, clubUUID, paginationLinks } from "@/shared/state";

const { authenticatedFetch } = useAuthenticatedFetch();
const API_URL = process.env.VUE_APP_ROOT_API;

export function useSessionBookings() {
  const isRequestBooking = ref(false);
  const errorBooking = ref(false);

  const fetchBookings = async (params: SearchBookingParams) => {
    try {
      isRequestBooking.value = true;
      errorBooking.value = false;
      sessionBookings.data = [];
      paginationLinks.value = 0;

      const body = {
        ...params,
        clubUUID: params.clubUUID ?? clubUUID.value,
      };

      const response = await authenticatedFetch(
        `${API_URL}session-bookings/search`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false,
        "",
        false,
      );

      if (response) {
        if (params?.type === "paginate") {
          sessionBookings.data = response?.data?.data ?? [];
          paginationLinks.value = response?.data ?? {};
          if (paginationLinks.value?.data) {
            delete paginationLinks.value.data;
          }
        } else if (params?.type === "all") {
          sessionBookings.data = response?.data ?? [];
        } else {
          sessionBookings.data = Array.isArray(response?.data)
            ? response.data
            : response?.data?.data ?? [];
        }
      }
    } catch (err: unknown) {
      errorBooking.value = true;
    } finally {
      isRequestBooking.value = false;
    }
  };

  const createBooking = async (
    payload: StoreBookingPayload,
  ): Promise<SessionBooking | null> => {
    try {
      isRequestBooking.value = true;
      errorBooking.value = false;

      const body = {
        ...payload,
        clubUUID: payload.clubUUID ?? clubUUID.value,
      };

      const response = await authenticatedFetch(
        `${API_URL}session-bookings/store/${clubUUID.value}`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        "",
        false,
      );

      if (response?.data) {
        const created: SessionBooking = response.data;
        sessionBookings.data?.unshift(created);
        return created;
      }
      return null;
    } catch (err: unknown) {
      errorBooking.value = true;
      return null;
    } finally {
      isRequestBooking.value = false;
    }
  };

  const markAttendance = async (
    payload: MarkAttendancePayload,
  ): Promise<Boolean | null> => {
    try {
      isRequestBooking.value = true;
      errorBooking.value = false;

      const response = await authenticatedFetch(
        `${API_URL}session-bookings/mark-attendance`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
        true,
        "",
        false,
      );

      if (response?.data) {
        const updated: SessionBooking = response.data;
        const index = sessionBookings.data?.findIndex(
          (b) => b.id === updated.id,
        );
        if (index !== undefined && index !== -1 && sessionBookings.data) {
          sessionBookings.data[index] = updated;
        }
        return true;
      }
       return false;
    } catch (err: unknown) {
      errorBooking.value = true;
     return false;
    } finally {
      isRequestBooking.value = false;
    }
  };

  const deleteBooking = async (id: number) => {
    try {
      isRequestBooking.value = true;
      errorBooking.value = false;
      await authenticatedFetch(
        `${API_URL}session-bookings/delete/${id}/${clubUUID.value}`,
        { method: "DELETE" },
        true,
        "",
        true,
      );

      const index = sessionBookings.data.findIndex((b) => b.id === id);
      if (index !== -1) {
        sessionBookings.data.splice(index, 1);
      }

      isRequestBooking.value = false;
      return true
    } catch (e) {
      errorBooking.value = true;
      isRequestBooking.value = false;
       return false
    }
  };
  return {
    deleteBooking,
    isRequestBooking,
    errorBooking,
    fetchBookings,
    createBooking,
    markAttendance,
  };
}
